import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { toast } from "react-toastify";
import { getOnboardingProfile, saveOnboardingProfile } from "./api/tenantApi";
import { notifyTeam, formatLines } from "./utils/notifyTeam";
import { CountryISOCodes } from "./utils/CountryISOCodes";
import { track } from "./analytics/analytics";
import {
  CREDIT_PRODUCTS,
  ONBOARDING_VERSION,
  PRODUCT_TERM_FIELDS,
  getCreditProduct,
} from "./onboardingConfig";

const C = {
  ink: "#102A43",
  blue: "#246BFD",
  pale: "#F4F7FB",
  muted: "#62748A",
  line: "#DDE6F0",
  green: "#178A55",
};

const SETUP_SECTIONS = [
  { title: "Company", caption: "Your business profile", icon: BusinessRoundedIcon },
  { title: "Credit model", caption: "How you extend credit", icon: CreditScoreRoundedIcon },
  { title: "Typical terms", caption: "Your usual commercial terms", icon: TuneRoundedIcon },
  { title: "Review", caption: "Confirm and personalize", icon: FactCheckRoundedIcon },
];

const FLOW = [
  { title: "Summary", caption: "Completed and remaining", icon: FactCheckRoundedIcon },
  ...SETUP_SECTIONS,
];

const INDUSTRIES = [
  "Financial services",
  "Payments / Fintech",
  "Retail / E-commerce",
  "Manufacturing",
  "Logistics / Transportation",
  "Software / Technology",
  "Healthcare",
  "Construction",
  "Other",
];

const doneKey = (subId) => `crediarc_onboarding_v${ONBOARDING_VERSION}_${subId || "anon"}`;
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: "#fff",
    "& fieldset": { borderColor: C.line },
    "&:hover fieldset": { borderColor: "#9EB5CC" },
    "&.Mui-focused fieldset": { borderColor: C.blue, borderWidth: 1.5 },
  },
};

const Label = ({ children, optional }) => (
  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.ink, mb: 0.75 }}>
    {children}{optional && <Box component="span" sx={{ color: C.muted, fontWeight: 500 }}> · Optional</Box>}
  </Typography>
);

const SummaryRow = ({ label, value }) => !value ? null : (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3, py: 1.15 }}>
    <Typography sx={{ color: C.muted, fontSize: 13.5 }}>{label}</Typography>
    <Typography sx={{ color: C.ink, fontSize: 13.5, fontWeight: 700, textAlign: "right" }}>{value}</Typography>
  </Box>
);

export const buildOnboardingStatus = ({
  company = {},
  productId = "",
  terms = {},
  portfolio = {},
} = {}) => {
  const product = getCreditProduct(productId);
  const termFields = PRODUCT_TERM_FIELDS[productId] || [];
  const companyMissing = [
    !String(company.name || "").trim() && "company name",
    !String(company.country || "").trim() && "headquarters country",
    !company.baseCurrency && "portfolio currency",
    !company.industry && "industry",
    !company.size && "company size",
  ].filter(Boolean);
  const termsMissing = termFields
    .filter((field) => !field.optional && !String(terms[field.key] || "").trim())
    .map((field) => field.label.toLowerCase());
  const reviewMissing = [
    !portfolio.monthlyVolume && "monthly credit volume",
    !portfolio.activeCustomers && "active credit customers",
    !portfolio.primaryGoal && "primary goal",
  ].filter(Boolean);
  const item = (label, value) => value ? { label, value: String(value) } : null;
  const companyItems = [
    item("Company", company.name),
    item("Country", company.country),
    item("Currency", company.baseCurrency),
    item("Industry", company.industry),
    item("Size", company.size),
    item("Website", company.website),
    item("Role", company.role),
  ].filter(Boolean);
  const termItems = termFields
    .map((field) => item(field.label, terms[field.key]))
    .filter(Boolean);
  const portfolioItems = [
    item("Monthly volume", portfolio.monthlyVolume),
    item("Active customers", portfolio.activeCustomers),
    item("Primary goal", portfolio.primaryGoal),
  ].filter(Boolean);

  return [
    {
      key: "company",
      ...SETUP_SECTIONS[0],
      complete: companyMissing.length === 0,
      detail: companyMissing.length ? `Still needed: ${companyMissing.join(", ")}` : `${company.name} · ${company.country}`,
      items: companyItems,
    },
    {
      key: "creditModel",
      ...SETUP_SECTIONS[1],
      complete: Boolean(product),
      detail: product ? product.name : "Still needed: select the primary credit model",
      items: product ? [item("Primary model", product.name)] : [],
    },
    {
      key: "terms",
      ...SETUP_SECTIONS[2],
      complete: Boolean(product) && termsMissing.length === 0,
      detail: !product
        ? "Choose a credit model first"
        : termsMissing.length ? `Still needed: ${termsMissing.join(", ")}` : `${termFields.length} commercial term fields captured`,
      items: termItems,
    },
    {
      key: "review",
      ...SETUP_SECTIONS[3],
      complete: reviewMissing.length === 0,
      detail: reviewMissing.length ? `Still needed: ${reviewMissing.join(", ")}` : `${portfolio.monthlyVolume} · ${portfolio.activeCustomers} customers`,
      items: portfolioItems,
    },
  ];
};

export default function OnboardingQuestionnaire({ adminTenantId = null, onClose = null }) {
  const isAdmin = useSelector((s) => s.userInfo.isAdmin);
  const isDistributor = useSelector((s) => s.userInfo.isDistributor);
  const email = useSelector((s) => s.userInfo.email);
  const tenantId = useSelector((s) => s.userInfo.tenantId);
  const effectiveTenantId = adminTenantId || tenantId;
  const reduxSubId = useSelector((s) => s.userInfo.subId);
  const subId = reduxSubId || (typeof window !== "undefined" ? localStorage.getItem("subId") : null);
  const fullScreen = useMediaQuery("(max-width:700px)");
  const previewMode = process.env.NODE_ENV === "development" &&
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("previewOnboarding") === "1";

  const [open, setOpen] = useState(previewMode);
  const [step, setStep] = useState(0);
  const [company, setCompany] = useState({});
  const [productId, setProductId] = useState("");
  const [terms, setTerms] = useState({});
  const [portfolio, setPortfolio] = useState({});
  const [saving, setSaving] = useState(false);
  const [finished, setFinished] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [sharedProfile, setSharedProfile] = useState(null);
  const checkedRef = useRef(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!open || previewMode || startedRef.current) return;
    startedRef.current = true;
    track("onboarding_started", { step: 1, stepName: SETUP_SECTIONS[0].title, sharedTenantOnboarding: true });
  }, [open, previewMode]);

  useEffect(() => {
    if (previewMode || checkedRef.current || !subId || (isAdmin && !adminTenantId) || (isDistributor && !adminTenantId)) return;
    checkedRef.current = true;

    (async () => {
      try {
        // The tenant record is authoritative. Every seat loads the same answers and
        // continues from the same company-wide draft.
        const response = await getOnboardingProfile(adminTenantId);
        const tenantOnboarding = response?.data;
        if (tenantOnboarding?.completed && !adminTenantId) {
          localStorage.setItem(doneKey(subId), "done");
          return;
        }
        if (tenantOnboarding) {
          setSharedProfile(tenantOnboarding);
          setCompany(tenantOnboarding.company || {});
          setProductId(tenantOnboarding.creditProfile?.productId || "");
          setTerms(tenantOnboarding.creditProfile?.terms || {});
          setPortfolio(tenantOnboarding.creditProfile?.portfolio || {});
        }
        setStep(0);
        setOpen(true);
      } catch (error) {
        console.warn("Company onboarding status check failed:", error?.message);
        // Do not trust a stale per-user completion flag when the shared company
        // record cannot be read. Showing onboarding is safer than hiding work.
        setOpen(true);
      }
    })();
  }, [adminTenantId, subId, isAdmin, isDistributor, previewMode]);

  const selectedProduct = getCreditProduct(productId);
  const termFields = PRODUCT_TERM_FIELDS[productId] || [];

  const companyValid = Boolean(company.name?.trim() && company.country?.trim() && company.baseCurrency && company.industry && company.size);
  const termsValid = termFields.every((field) => field.optional || String(terms[field.key] || "").trim());
  const reviewValid = Boolean(portfolio.monthlyVolume && portfolio.activeCustomers && portfolio.primaryGoal);
  const sectionStatus = useMemo(
    () => buildOnboardingStatus({ company, productId, terms, portfolio }),
    [company, portfolio, productId, terms]
  );
  const completedSections = sectionStatus.filter((section) => section.complete).length;
  const firstIncompleteIndex = sectionStatus.findIndex((section) => !section.complete);
  const firstIncompleteStep = firstIncompleteIndex === -1 ? SETUP_SECTIONS.length : firstIncompleteIndex + 1;
  const canContinue = step === 0 || [companyValid, Boolean(productId), termsValid, reviewValid][step - 1];

  const title = [
    "Your shared onboarding progress",
    "Tell us about your company",
    "What type of credit risk do you manage?",
    `What are your usual ${selectedProduct?.shortName || "credit"} terms?`,
    "Review your operating profile",
  ][step];

  const subtitle = [
    "Everyone in this workspace sees and continues the same setup. Here is what your team has already completed and what remains.",
    "We use this to set the right portfolio defaults and reporting context.",
    "Choose the primary model you want CrediArc to assess. You can add more later.",
    "Use portfolio averages. Exact values are not required, and you can update these later.",
    "These answers will personalize limits, monitoring, and risk insights for your team.",
  ][step];

  const setCompanyValue = (key, value) => setCompany((current) => ({ ...current, [key]: value }));
  const setTermValue = (key, value) => setTerms((current) => ({ ...current, [key]: value }));
  const setPortfolioValue = (key, value) => setPortfolio((current) => ({ ...current, [key]: value }));

  const selectProduct = (id) => {
    if (id !== productId) setTerms({});
    setProductId(id);
    const product = getCreditProduct(id);
    track("onboarding_credit_model_selected", {
      step: 2,
      stepName: FLOW[1].title,
      productId: id,
      productName: product?.shortName || product?.name || id,
    });
  };

  const basePayload = useMemo(() => ({
    version: ONBOARDING_VERSION,
    company,
    creditProfile: {
      productId,
      productName: selectedProduct?.name || "",
      terms,
      portfolio,
    },
  }), [company, portfolio, productId, selectedProduct, terms]);

  const continueOnboarding = async () => {
    if (step === 0) {
      setStep(firstIncompleteStep);
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      if (!previewMode) {
        await saveOnboardingProfile({
          ...basePayload,
          completed: false,
          currentStep: Math.min(step, SETUP_SECTIONS.length - 1),
        }, adminTenantId);
      }
      track("onboarding_step_completed", {
        step,
        stepName: SETUP_SECTIONS[step - 1].title,
        ...(step === 2 && productId ? { productId } : {}),
        sharedTenantOnboarding: true,
      });
      setSharedProfile((current) => ({
        ...(current || {}),
        ...basePayload,
        completed: false,
        currentStep: Math.min(step, SETUP_SECTIONS.length - 1),
        updatedAt: new Date().toISOString(),
      }));
      setStep((current) => current + 1);
    } catch (error) {
      setSaveError(error?.response?.data?.message || error?.message || "We couldn't save your team's progress. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const openSummarySection = (index) => {
    setStep(index + 1);
    setSaveError("");
  };

  const displayTermValue = (field) => {
    const value = terms[field.key];
    if (!value) return "";
    const prefix = field.prefix === "$" ? `${company.baseCurrency || "USD"} ` : (field.prefix || "");
    return `${prefix}${value}${field.suffix ? ` ${field.suffix}` : ""}`;
  };

  const finish = async () => {
    setSaving(true);
    setSaveError("");
    const completedAt = new Date().toISOString();
    const completedPayload = {
      ...basePayload,
      completed: true,
      completedAt,
      currentStep: SETUP_SECTIONS.length - 1,
    };

    try {
      if (!previewMode) {
        if (!subId) throw new Error("Your user profile is still loading. Please try again.");
        // Firestore is the single company-wide source of record for every answer.
        await saveOnboardingProfile(completedPayload, adminTenantId);
        localStorage.setItem(doneKey(subId), "done");
        track("onboarding_completed", {
          step: SETUP_SECTIONS.length,
          stepName: SETUP_SECTIONS[SETUP_SECTIONS.length - 1].title,
          productId,
          productName: selectedProduct?.shortName || selectedProduct?.name,
          sharedTenantOnboarding: true,
        });
        notifyTeam({
          subject: `New CrediArc client profile${company.name ? `: ${company.name}` : ""}`,
          message: `A user completed the credit-risk onboarding.\n\n${formatLines({
            Email: email,
            Tenant: effectiveTenantId,
            Company: company.name,
            Country: company.country,
            Currency: company.baseCurrency,
            Industry: company.industry,
            "Credit model": selectedProduct?.name,
            "Monthly volume": portfolio.monthlyVolume,
            "Active customers": portfolio.activeCustomers,
            Goal: portfolio.primaryGoal,
            ...terms,
          })}`,
        });
      }
      setFinished(true);
      toast.success(previewMode ? "Preview completed" : "Your workspace profile is ready");
    } catch (error) {
      track("onboarding_save_failed", { step: SETUP_SECTIONS.length, stepName: SETUP_SECTIONS[SETUP_SECTIONS.length - 1].title });
      setSaveError(error?.response?.data?.message || error?.message || "We couldn't save your profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Distributor identities represent a broker/distribution workspace and intentionally do not
  // carry a tenantId. Tenant onboarding therefore does not apply to their own distributor home.
  // Platform admins can still open a specific client's onboarding through adminTenantId.
  if (isDistributor && !adminTenantId) return null;

  const renderField = (field) => (
    <Box key={field.key}>
      <Label optional={field.optional}>{field.label}</Label>
      {field.type === "select" ? (
        <FormControl fullWidth size="small" sx={fieldSx}>
          <Select value={terms[field.key] || ""} displayEmpty onChange={(event) => setTermValue(field.key, event.target.value)}>
            <MenuItem value="" disabled><Typography sx={{ color: "#93A4B5", fontSize: 14 }}>Select an option</Typography></MenuItem>
            {field.options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </Select>
        </FormControl>
      ) : (
        <TextField
          fullWidth
          size="small"
          type={field.type === "number" ? "number" : "text"}
          value={terms[field.key] || ""}
          placeholder={field.placeholder}
          onChange={(event) => setTermValue(field.key, event.target.value)}
          inputProps={field.type === "number" ? { min: 0, step: "any" } : undefined}
          InputProps={{
            startAdornment: field.prefix ? <InputAdornment position="start">{field.prefix === "$" ? (company.baseCurrency || "USD") : field.prefix}</InputAdornment> : undefined,
            endAdornment: field.suffix ? <InputAdornment position="end">{field.suffix}</InputAdornment> : undefined,
          }}
          sx={fieldSx}
        />
      )}
    </Box>
  );

  const body = () => {
    if (step === 0) return (
      <Box>
        <Alert severity="info" sx={{ mb: 2.25, borderRadius: "10px" }}>
          This is one shared onboarding for the whole company. Any teammate can continue it, and every saved section is visible to everyone in this workspace.
        </Alert>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: 14.5, fontWeight: 900, color: C.ink }}>
              {completedSections} of {SETUP_SECTIONS.length} sections complete
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.25 }}>
              {sharedProfile?.updatedAt
                ? `Shared progress last saved ${new Date(sharedProfile.updatedAt).toLocaleString()}`
                : "No shared progress has been saved yet"}
            </Typography>
          </Box>
          <Chip
            label={`${Math.round((completedSections / SETUP_SECTIONS.length) * 100)}%`}
            sx={{ fontWeight: 900, color: C.blue, background: "#EAF1FF" }}
          />
        </Box>
        <Box sx={{ height: 8, borderRadius: 999, background: "#E8EEF5", overflow: "hidden", mb: 2.25 }}>
          <Box sx={{ width: `${(completedSections / SETUP_SECTIONS.length) * 100}%`, height: "100%", background: C.blue, transition: "width .2s ease" }} />
        </Box>
        <Box sx={{ display: "grid", gap: 1.25 }}>
          {sectionStatus.map((section, index) => {
            const Icon = section.icon;
            return (
              <Box
                key={section.key}
                role="button"
                tabIndex={0}
                onClick={() => openSummarySection(index)}
                onKeyDown={(event) => event.key === "Enter" && openSummarySection(index)}
                sx={{ display: "grid", gridTemplateColumns: "38px 1fr auto", alignItems: "center", gap: 1.4, p: 1.6, border: `1px solid ${section.complete ? "#BFE8D1" : C.line}`, borderRadius: "12px", background: section.complete ? "#F4FBF7" : "#fff", cursor: "pointer", "&:hover, &:focus": { borderColor: section.complete ? C.green : C.blue, outline: "none" } }}
              >
                <Box sx={{ width: 36, height: 36, borderRadius: "10px", display: "grid", placeItems: "center", color: section.complete ? C.green : C.blue, background: section.complete ? "#E1F6EA" : "#EAF1FF" }}>
                  {section.complete ? <CheckCircleRoundedIcon sx={{ fontSize: 20 }} /> : <Icon sx={{ fontSize: 20 }} />}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 850, color: C.ink }}>{section.title}</Typography>
                  <Typography sx={{ fontSize: 12.25, color: C.muted, mt: 0.2, lineHeight: 1.4 }}>{section.detail}</Typography>
                  {section.items.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mt: 0.8 }}>
                      {section.items.map((entry) => (
                        <Box key={entry.label} sx={{ px: 0.8, py: 0.35, borderRadius: "6px", background: section.complete ? "#E8F7EF" : C.pale }}>
                          <Typography sx={{ fontSize: 10.75, color: C.muted }}>
                            <Box component="span" sx={{ fontWeight: 800, color: C.ink }}>{entry.label}:</Box> {entry.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Chip size="small" label={section.complete ? "Done" : "Left"} sx={{ height: 22, fontWeight: 800, color: section.complete ? C.green : "#9A6700", background: section.complete ? "#E1F6EA" : "#FFF4CC" }} />
              </Box>
            );
          })}
        </Box>
      </Box>
    );

    if (step === 1) return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.25 }}>
        <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
          <Label>Company name</Label>
          <TextField fullWidth size="small" autoFocus value={company.name || ""} placeholder="Acme Finance" onChange={(e) => setCompanyValue("name", e.target.value)} sx={fieldSx} />
        </Box>
        <Box>
          <Label>Headquarters country</Label>
          <Autocomplete
            fullWidth
            size="small"
            autoHighlight
            openOnFocus
            options={CountryISOCodes}
            value={CountryISOCodes.find((option) => option.name === company.country) || null}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option["alpha-2"] === value["alpha-2"]}
            onChange={(_, option) => setCompany((current) => ({
              ...current,
              country: option?.name || "",
              countryCode: option?.["alpha-2"] || "",
            }))}
            renderInput={(params) => <TextField {...params} placeholder="Search for a country" sx={fieldSx} />}
          />
        </Box>
        <Box><Label>Portfolio currency</Label><FormControl fullWidth size="small" sx={fieldSx}><Select displayEmpty value={company.baseCurrency || ""} onChange={(e) => setCompanyValue("baseCurrency", e.target.value)}><MenuItem value="" disabled><Typography sx={{ color: "#93A4B5", fontSize: 14 }}>Select currency</Typography></MenuItem>{["USD", "EUR", "GBP", "ILS", "CAD", "AUD", "Other"].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Box>
        <Box><Label>Industry</Label><FormControl fullWidth size="small" sx={fieldSx}><Select displayEmpty value={company.industry || ""} onChange={(e) => setCompanyValue("industry", e.target.value)}><MenuItem value="" disabled><Typography sx={{ color: "#93A4B5", fontSize: 14 }}>Select industry</Typography></MenuItem>{INDUSTRIES.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Box>
        <Box><Label>Company size</Label><FormControl fullWidth size="small" sx={fieldSx}><Select displayEmpty value={company.size || ""} onChange={(e) => setCompanyValue("size", e.target.value)}><MenuItem value="" disabled><Typography sx={{ color: "#93A4B5", fontSize: 14 }}>Number of employees</Typography></MenuItem>{["1–10", "11–50", "51–200", "201–1,000", "1,000+"].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Box>
        <Box><Label>Company website <Box component="span" sx={{ color: C.muted, fontWeight: 500 }}>· Optional</Box></Label><TextField fullWidth size="small" value={company.website || ""} placeholder="www.company.com" onChange={(e) => setCompanyValue("website", e.target.value)} sx={fieldSx} /></Box>
        <Box><Label>Your role <Box component="span" sx={{ color: C.muted, fontWeight: 500 }}>· Optional</Box></Label><TextField fullWidth size="small" value={company.role || ""} placeholder="Head of Credit Risk" onChange={(e) => setCompanyValue("role", e.target.value)} sx={fieldSx} /></Box>
      </Box>
    );

    if (step === 2) return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
        {CREDIT_PRODUCTS.map((product) => {
          const selected = product.id === productId;
          return (
            <Box key={product.id} role="button" tabIndex={0} onClick={() => selectProduct(product.id)} onKeyDown={(e) => e.key === "Enter" && selectProduct(product.id)}
              sx={{ p: 2, borderRadius: "12px", border: `1.5px solid ${selected ? C.blue : C.line}`, background: selected ? "#F2F6FF" : "#fff", cursor: "pointer", transition: "all .15s ease", outline: "none", "&:hover, &:focus": { borderColor: C.blue, transform: "translateY(-1px)", boxShadow: "0 5px 18px rgba(36,107,253,.08)" } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: C.ink }}>{product.name}</Typography>
                <Box sx={{ width: 18, height: 18, borderRadius: "50%", border: `1.5px solid ${selected ? C.blue : "#B8C6D5"}`, background: selected ? C.blue : "transparent", boxShadow: selected ? "inset 0 0 0 4px #fff" : "none", flexShrink: 0 }} />
              </Box>
              <Typography sx={{ color: C.muted, fontSize: 12.5, lineHeight: 1.45, mt: 0.6 }}>{product.description}</Typography>
            </Box>
          );
        })}
      </Box>
    );

    if (step === 3) return (
      <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
          <Chip label={selectedProduct?.shortName} size="small" sx={{ color: C.blue, background: "#EAF1FF", fontWeight: 800 }} />
          <Typography sx={{ fontSize: 12.5, color: C.muted }}>All values should represent a typical transaction or customer.</Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.25 }}>{termFields.map(renderField)}</Box>
      </>
    );

    return (
      <Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.25 }}>
          <Box><Label>Monthly credit volume</Label><FormControl fullWidth size="small" sx={fieldSx}><Select displayEmpty value={portfolio.monthlyVolume || ""} onChange={(e) => setPortfolioValue("monthlyVolume", e.target.value)}><MenuItem value="" disabled><Typography sx={{ color: "#93A4B5", fontSize: 14 }}>Select range</Typography></MenuItem>{["Under $100K", "$100K–$1M", "$1M–$10M", "$10M–$50M", "$50M+"].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Box>
          <Box><Label>Active credit customers</Label><FormControl fullWidth size="small" sx={fieldSx}><Select displayEmpty value={portfolio.activeCustomers || ""} onChange={(e) => setPortfolioValue("activeCustomers", e.target.value)}><MenuItem value="" disabled><Typography sx={{ color: "#93A4B5", fontSize: 14 }}>Select range</Typography></MenuItem>{["Under 100", "100–1,000", "1,001–10,000", "10,001–100,000", "100,000+"].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Box>
          <Box sx={{ gridColumn: { sm: "1 / -1" } }}><Label>What should CrediArc help with first?</Label><FormControl fullWidth size="small" sx={fieldSx}><Select displayEmpty value={portfolio.primaryGoal || ""} onChange={(e) => setPortfolioValue("primaryGoal", e.target.value)}><MenuItem value="" disabled><Typography sx={{ color: "#93A4B5", fontSize: 14 }}>Choose your main goal</Typography></MenuItem>{["Automate underwriting", "Set and manage credit limits", "Monitor portfolio risk", "Manage credit insurance", "Manage lender process and underwriting", "Reduce defaults and late payments", "Improve collections prioritization", "Prepare reporting and compliance"].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Box>
        </Box>
        <Box sx={{ mt: 2.5, p: 2.25, border: `1px solid ${C.line}`, borderRadius: "12px", background: C.pale }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}><Typography sx={{ fontSize: 14, fontWeight: 800, color: C.ink }}>{company.name}</Typography><Chip label={selectedProduct?.shortName} size="small" sx={{ fontWeight: 800, color: C.blue, background: "#EAF1FF" }} /></Box>
          <SummaryRow label="Industry" value={company.industry} />
          {termFields.slice(0, 4).map((field) => <SummaryRow key={field.key} label={field.label} value={displayTermValue(field)} />)}
        </Box>
      </Box>
    );
  };

  if (!open) return null;

  const closeQuestionnaire = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} fullScreen={fullScreen} disableEscapeKeyDown maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: fullScreen ? 0 : "18px", overflow: "hidden", maxHeight: fullScreen ? "100%" : "min(760px, 94vh)" } }}>
      {adminTenantId && (
        <IconButton
          aria-label="Close client onboarding"
          onClick={closeQuestionnaire}
          sx={{ position: "absolute", top: 10, right: 10, zIndex: 3, color: C.muted, background: "rgba(255,255,255,.9)", "&:hover": { background: "#fff" } }}
        >
          <CloseRoundedIcon />
        </IconButton>
      )}
      {!finished ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "230px 1fr" }, minHeight: { md: 650 } }}>
          <Box sx={{ background: C.ink, color: "#fff", p: { xs: 2.25, md: 3 }, display: "flex", flexDirection: { xs: "row", md: "column" }, alignItems: { xs: "center", md: "stretch" }, gap: 2 }}>
            <Box sx={{ flex: { xs: 1, md: "initial" } }}>
              <Typography sx={{ fontSize: 18, fontWeight: 900, letterSpacing: "-.02em" }}>CrediArc</Typography>
              <Typography sx={{ fontSize: 12, color: "#AFC2D7", mt: 0.25 }}>Workspace setup · about 3 minutes</Typography>
            </Box>
            <Box sx={{ display: { xs: "flex", md: "block" }, gap: 0.75, mt: { md: 4 } }}>
              {FLOW.map((item, index) => {
                const Icon = item.icon;
                const active = index === step;
                const complete = index > 0 && Boolean(sectionStatus[index - 1]?.complete);
                return (
                  <Box key={item.title} sx={{ display: "flex", gap: 1.35, alignItems: "center", mb: { md: 2.15 } }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: "9px", display: "grid", placeItems: "center", background: active ? C.blue : complete ? "rgba(38,194,129,.18)" : "rgba(255,255,255,.08)", color: active ? "#fff" : complete ? "#62D7A2" : "#8EA6BE" }}>
                      {complete ? <CheckCircleRoundedIcon sx={{ fontSize: 17 }} /> : <Icon sx={{ fontSize: 17 }} />}
                    </Box>
                    <Box sx={{ display: { xs: "none", md: "block" } }}><Typography sx={{ fontSize: 13.5, fontWeight: 800, color: active || complete ? "#fff" : "#9CB0C4" }}>{item.title}</Typography><Typography sx={{ fontSize: 11.5, color: "#7F97AF" }}>{item.caption}</Typography></Box>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ display: { xs: "none", md: "block" }, mt: "auto", p: 1.6, borderRadius: "10px", background: "rgba(255,255,255,.06)" }}><Typography sx={{ fontSize: 11.5, lineHeight: 1.5, color: "#AFC2D7" }}>Your answers are private to your CrediArc workspace and can be updated later.</Typography></Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
            <Box sx={{ px: { xs: 2.5, sm: 4.5 }, pt: { xs: 3, sm: 4 }, pb: 2 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: C.blue, textTransform: "uppercase", letterSpacing: ".08em", mb: 0.8 }}>
                {step === 0 ? "Shared workspace setup" : `Step ${step} of ${SETUP_SECTIONS.length}`}
              </Typography>
              <Typography sx={{ fontSize: { xs: 23, sm: 27 }, fontWeight: 900, letterSpacing: "-.025em", color: C.ink, lineHeight: 1.18 }}>{title}</Typography>
              <Typography sx={{ fontSize: 13.5, color: C.muted, mt: 0.8, lineHeight: 1.5 }}>{subtitle}</Typography>
            </Box>
            <Box sx={{ px: { xs: 2.5, sm: 4.5 }, py: 1.5, overflowY: "auto", flex: 1 }}>{body()}</Box>
            <Box sx={{ px: { xs: 2.5, sm: 4.5 }, pt: 2, pb: 3, mt: 1 }}>
              {saveError && <Alert severity="error" sx={{ mb: 1.5 }}>{saveError}</Alert>}
              <Divider sx={{ mb: 2.25, borderColor: C.line }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Button disabled={step === 0 || saving} onClick={() => { setStep((current) => current - 1); setSaveError(""); }} startIcon={<ArrowBackRoundedIcon />} sx={{ textTransform: "none", color: C.ink, fontWeight: 700, visibility: step === 0 ? "hidden" : "visible" }}>Back</Button>
                <Button disabled={!canContinue || saving} onClick={() => step === FLOW.length - 1 ? finish() : continueOnboarding()} variant="contained" endIcon={saving ? null : step === FLOW.length - 1 ? <CheckCircleRoundedIcon /> : <ArrowForwardRoundedIcon />} sx={{ textTransform: "none", fontWeight: 800, borderRadius: "10px", px: 3, py: 1.05, boxShadow: "none", background: C.blue, "&:hover": { background: "#165BE0", boxShadow: "0 6px 16px rgba(36,107,253,.22)" } }}>
                  {saving
                    ? <><CircularProgress color="inherit" size={17} sx={{ mr: 1 }} />Saving…</>
                    : step === FLOW.length - 1 ? "Finish setup"
                      : step === 0 ? (completedSections ? "Continue setup" : "Start setup")
                        : "Save & continue"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ px: 4, py: 7, textAlign: "center", minHeight: 460, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ width: 68, height: 68, borderRadius: "20px", display: "grid", placeItems: "center", color: C.green, background: "#E9F8F0", mb: 2 }}><CheckCircleRoundedIcon sx={{ fontSize: 40 }} /></Box>
          <Typography sx={{ fontSize: 28, fontWeight: 900, color: C.ink, letterSpacing: "-.025em" }}>Your workspace is ready</Typography>
          <Typography sx={{ fontSize: 14.5, color: C.muted, maxWidth: 470, mt: 1, mb: 3, lineHeight: 1.6 }}>CrediArc will use your {selectedProduct?.shortName} profile to tailor underwriting, monitoring, and credit-limit insights.</Typography>
          <Button onClick={closeQuestionnaire} variant="contained" sx={{ textTransform: "none", fontWeight: 800, background: C.blue, borderRadius: "10px", px: 4, py: 1.1, boxShadow: "none" }}>
            {previewMode ? "Close preview" : adminTenantId ? "Close onboarding" : "Go to my dashboard"}
          </Button>
        </Box>
      )}
    </Dialog>
  );
}
